/**
 * Fetches the live price (in USD) for a given token symbol from CoinMarketCap.
 *
 * @param tokenSymbol - The token symbol (e.g. "BTC", "ETH").
 * @returns The live price as a number.
 * @throws Error if the API key is missing or the request fails.
 */

export async function fetchLiveTokenPrice(
  tokenSymbol: string
): Promise<number> {
  const apiKey = process.env.CMC_API_KEY;
  if (!apiKey) {
    throw new Error("Missing CoinMarketCap API key in environment variables");
  }

  const url = `https://pro-api.coinmarketcap.com/v1/cryptocurrency/quotes/latest?symbol=${tokenSymbol}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "X-CMC_PRO_API_KEY": apiKey,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching price for ${tokenSymbol}: ${response.statusText}`
    );
  }

  const data = await response.json();
  const price = data.data[tokenSymbol]?.quote?.USD?.price;

  if (price === undefined) {
    throw new Error(`Could not retrieve price for token: ${tokenSymbol}`);
  }

  return price;
}

export const getLiveTokenPriceCoinGecko = async (
  token?: string
): Promise<number> => {
  if (token == null || token.trim() === "") {
    console.error("getLiveTokenPrice: token is undefined or empty");
    throw new Error("Token is required to fetch live price");
  }

  try {
    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?vs_currencies=usd&ids=${token}`
    );
    const data = await response.json();
    return data[token].usd;
  } catch (error) {
    console.error("Error in getLiveTokenPrice:", error);
    return 0;
  }
};

// Cache for SPON token price
interface PriceCache {
  price: number;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_KEY = 'spon_price_cache';

const getCachedPrice = (): PriceCache | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn('Error reading cached price from localStorage:', error);
  }
  return null;
};

const setCachedPrice = (price: number, timestamp: number): void => {
  try {
    const cacheData: PriceCache = { price, timestamp };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Error saving cached price to localStorage:', error);
  }
};

export const getCachedSPONPrice = async (): Promise<number> => {
  const now = Date.now();
  
  // Check if we have a valid cached price
  const cachedPrice = getCachedPrice();
  if (cachedPrice && (now - cachedPrice.timestamp) < CACHE_DURATION) {
    return cachedPrice.price;
  }
  
  try {
    // Fetch fresh price from CoinGecko
    const price = await getLiveTokenPriceCoinGecko("spheron-network");
    
    // Update cache
    setCachedPrice(price, now);
    
    return price;
  } catch (error) {
    console.error("Error fetching cached SPON price:", error);
    
    // If we have a cached price but it's expired, return it as fallback
    if (cachedPrice) {
      console.warn("Using expired cached SPON price as fallback");
      return cachedPrice.price;
    }
    
    return 0;
  }
};
